import { DownloadOutlined, OpenInNewOutlined } from "@mui/icons-material";
import { Box, Stack, StackProps, Typography } from "@mui/material";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { ProductPermission } from "commons/enum/permission";
import { useCheckPermission } from "commons/hooks/use-check-permission";
import { downloadFile, isFileType } from "commons/ultilities";
import { observer } from "mobx-react-lite";
import { IFileProperties } from "mobx-stores/_models/products/product";
import { useStores } from "mobx-stores/_root-store";
import { MouseEventHandler, useCallback, useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import { RemoveIconButton } from "share-components/mui-wrap/button/action-button";
import { XIconButton } from "share-components/mui-wrap/button/x-icon-button";
export const FileList = observer(() => {
  const {
    productManagement: { productDetail },
  } = useStores();

  return (
    <Stack direction={"row"} alignItems={"center"} flexWrap={"wrap"}>
      {productDetail?.fileUploads?.map((x) => (
        <ShowFile file={x} key={x.name}></ShowFile>
      ))}
    </Stack>
  );
});
type ShowFileProps = {
  file: IFileProperties;
};
export const ShowFile = ({ file }: ShowFileProps) => {
  const [url, setUrl] = useState<string>();
  const {
    deleteConfirmationStore: { setDeletionCallback, setProp },
    productManagement: { getProductDetail, productDetail },
  } = useStores();

  const createUrl = useCallback(async () => {
    if (!file.name) return;
    const res = await productBussiness.getFile(file.name);
    setUrl(URL.createObjectURL(res));
  }, [file.name]);
  useEffect(() => {
    createUrl();
  }, [createUrl]);

  const openLinkInNewTab = useCallback(() => {
    if (!url) {
      console.error("Invalid URL.");
      return;
    }

    // Open the URL in a new tab
    const newTab = window.open(url, "_blank");

    if (!newTab) {
      console.error(
        "Failed to open a new tab. Please allow popups in your browser."
      );
    }
  }, [url]);

  const downloadHandler = useCallback(async () => {
    if (!file.name) return;
    const res = await productBussiness.downloadFile(file.name);
    downloadFile(res, file.originName || file.name);
  }, [file.name, file.originName]);

  const removeFileClickHandler = useCallback(() => {
    setDeletionCallback(async () => {
      if (!file.name) return;
      await productBussiness.deleteFile(file.name);
      if (productDetail?.id) {
        await getProductDetail(productDetail?.id);
      }
    });
    setProp("open", true);
  }, [
    file.name,
    getProductDetail,
    productDetail?.id,
    setDeletionCallback,
    setProp,
  ]);

  return (
    <FileItemContainer
      width={"300px"}
      height={"300px"}
      rowGap={"10px"}
      detailClickHandler={() => openLinkInNewTab()}
      downloadClickHandler={downloadHandler}
      removeClickHandler={removeFileClickHandler}
      fileName={file.originName || file.name || ""}
    >
      {url && file.name ? (
        isFileType(file.name, ["pdf"]) ? (
          <PdfThumnailItem file={url}></PdfThumnailItem>
        ) : (
          <ImageItem src={url} alt={file.originName || ""}></ImageItem>
        )
      ) : (
        <></>
      )}
    </FileItemContainer>
  );
};

type FileItemContainerProps = StackProps & {
  fileName?: string;
  detailClickHandler?: MouseEventHandler<HTMLButtonElement>;
  removeClickHandler?: MouseEventHandler<HTMLButtonElement>;
  downloadClickHandler?: MouseEventHandler<HTMLButtonElement>;
};
export const FileItemContainer = observer(
  ({
    detailClickHandler,
    removeClickHandler,
    downloadClickHandler,
    fileName,
    ...props
  }: FileItemContainerProps) => {
    const { allowAccess: allowDownloadFile } = useCheckPermission([
      ProductPermission.DownloadFile,
    ]);

    const { allowAccess: allowDeleteFile } = useCheckPermission([
      ProductPermission.DeleteFile,
    ]);

    return (
      <Stack
        {...props}
        direction={"row"}
        alignItems={"center"}
        sx={{
          ...props.sx,
          backgroundColor: "#e6e6e6",
          margin: "5px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {props.children}
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          spacing={1}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: `rgba(0, 0, 0, 0.5)`,
            padding: "5px",
            color: "#fff",
            overflow: "hidden",
            maxHeight: "50px",
          }}
        >
          <Stack direction={"row"}>
            <Typography
              variant="subtitle2"
              flexWrap={"wrap"}
              sx={{
                maxHeight: "40px",
                overflow: "hidden",
                whiteSpace: "normal",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                wordBreak: "break-word",
                "-webkit-line-clamp": "2",
                "-webkit-box-orient": "vertical",
              }}
            >
              {fileName}
            </Typography>
          </Stack>
          <Stack direction={"row"}>
            <XIconButton onClick={detailClickHandler} color="inherit">
              <OpenInNewOutlined fontSize="medium"></OpenInNewOutlined>
            </XIconButton>
            {allowDownloadFile && (
              <XIconButton onClick={downloadClickHandler} color="inherit">
                <DownloadOutlined fontSize="medium"></DownloadOutlined>
              </XIconButton>
            )}
            {allowDeleteFile && (
              <RemoveIconButton
                onClick={removeClickHandler}
                color="inherit"
              ></RemoveIconButton>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }
);

type ImageItemProps = {
  src: string;
  alt: string;
};
export const ImageItem = ({ src, alt }: ImageItemProps) => {
  return (
    <img
      src={`${src}`}
      alt={alt}
      loading="lazy"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
      }}
    ></img>
  );
};
type PdfThumnailItemProps = {
  file: File | string;
};
export const PdfThumnailItem = ({ file }: PdfThumnailItemProps) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        ".react-pdf__Document": {
          a: {
            cursor: "default",
          },
        },
      }}
    >
      <Document
        file={file}
        loading={"Đang tải..."}
        onLoadSuccess={() => console.log("Load sucess")}
        renderMode="canvas"
      >
        <Page
          pageNumber={1}
          width={300}
          height={300}
          renderMode="canvas"
          renderTextLayer={false}
          renderAnnotationLayer={false}
        ></Page>
      </Document>
    </Box>
  );
};
